/**
 * EFFET : la scène vidéo d'une partie entre amis — ma caméra, et mon ami en
 * vidéo OU en maillage (selon ce qu'il choisit d'envoyer), plus le choix « ton ami
 * voit ». Jamais de son : ni micro capté, ni voix envoyée. Le suivi du visage local tourne en continu :
 * il sert à la détection du jeu ET à l'envoi du maillage.
 */
import { drawMesh } from '../ar/meshRenderer';
import { makeProjector } from '../ar/projection';
import type { FaceSource } from '../detection/faceSource';
import type { Point3 } from '../detection/types';
import { createLocalFaceSource } from '../detection/localFaceSource';
import { createThrottle } from '../lib/throttle';
import { decodeMeshFrame, encodeMeshFrame } from '../mesh/codec';
import { createMeshReceiver } from '../mesh/receiver';
import type { PeerLink } from '../net/signaling';
import { h } from './dom';

const MESH_FPS = 15;

export type ShareMode = 'video' | 'mesh';

/**
 * Ce que l'ami reçoit : ma vidéo seule, ou rien (le maillage passe par la DataChannel).
 * Jamais de piste audio, même si le navigateur en fournissait une.
 */
export function outgoingStream(stream: MediaStream, mode: ShareMode): MediaStream | null {
  const video = mode === 'video' ? stream.getVideoTracks() : [];
  return video.length > 0 ? new MediaStream(video) : null;
}

export interface Stage {
  readonly element: HTMLElement;
  readonly controls: HTMLElement;
  readonly faceSource: FaceSource;
  /** Ma caméra brute (jamais filtrée). */
  readonly localVideo: HTMLVideoElement;
  /** Ce que j'ai choisi d'envoyer à mon ami. */
  shareMode(): ShareMode;
  /** Image de mon ami : sa vidéo reçue, ou son maillage s'il n'envoie pas de vidéo. */
  readonly remoteVideo: HTMLVideoElement;
  readonly remoteFigure: HTMLElement;
  readonly localFigure: HTMLElement;
  remoteShowsMesh(): boolean;
  /** Dernier maillage reçu (points vides si visage absent) et instant de réception. */
  remoteMesh(): { points: readonly Point3[]; t: number } | null;
  /** Statistique affichable (cadence du maillage reçu), vide en visio. */
  meshStats(): string;
  /**
   * Chifoumi : ma vidéo n'est plus envoyée (image noire chez l'ami) et la sienne est cachée,
   * le temps du coup. Mon retour vidéo, lui, reste affiché.
   */
  hideVideos(hidden: boolean): void;
  dispose(): void;
}

function shareToggle(onChange: (mode: ShareMode) => void): HTMLElement {
  const option = (mode: ShareMode, label: string, checked: boolean) => {
    const input = h('input', { type: 'radio', name: 'share', value: mode, testid: `mode-${mode}` });
    input.checked = checked;
    input.addEventListener('change', () => {
      onChange(mode);
    });
    return h('label', {}, input, ' ', label);
  };
  return h(
    'fieldset',
    { class: 'segmented' },
    h('legend', {}, 'Ton ami voit'),
    option('video', 'ta vidéo', true),
    option('mesh', 'seulement ton maillage', false),
  );
}

export function createStage(link: PeerLink, localStream: MediaStream): Stage {
  const local = h('video', {
    autoplay: '',
    muted: '',
    playsinline: '',
    testid: 'local-video',
    'aria-label': 'Toi',
  });
  local.muted = true;
  local.srcObject = localStream;
  const remote = h('video', {
    autoplay: '',
    muted: '',
    playsinline: '',
    testid: 'remote-video',
    'aria-label': 'Ton ami',
  });
  remote.muted = true; // jamais la voix de l'ami, même d'une ancienne version qui l'enverrait
  const meshCanvas = h('canvas', {
    width: '640',
    height: '480',
    testid: 'remote-mesh',
    hidden: '',
  });

  // Ce qui part chez l'ami : une COPIE de ma caméra, qu'on peut couper sans toucher à mon
  // retour vidéo.
  const sentVideo = localStream.getVideoTracks().map((t) => t.clone());
  const sent = new MediaStream(sentVideo);
  let mode: ShareMode = 'video';
  const controls = h(
    'div',
    { class: 'actions' },
    shareToggle((m) => {
      mode = m;
      link.setOutgoingStream(outgoingStream(sent, mode));
    }),
  );
  const remoteFigure = h(
    'figure',
    { class: 'frame remote' },
    remote,
    meshCanvas,
    h('figcaption', {}, 'Ton ami'),
  );
  const localFigure = h('figure', { class: 'frame self' }, local, h('figcaption', {}, 'Toi'));
  localFigure.dataset['sending'] = 'on';
  remoteFigure.dataset['covered'] = 'false';
  const element = h('div', { class: 'stage' }, localFigure, remoteFigure);

  // Flux reçu : vidéo si l'ami en envoie une, sinon son maillage.
  let remoteHasVideo = false;
  const offStream = link.onRemoteStream((stream) => {
    remote.srcObject = stream;
    remoteHasVideo = !!stream && stream.getVideoTracks().length > 0;
  });
  link.setOutgoingStream(outgoingStream(sent, mode));

  // Maillage : envoi (si je l'ai choisi) et réception.
  const faceSource = createLocalFaceSource(local);
  const throttle = createThrottle(MESH_FPS);
  let seq = 0;
  const offFace = faceSource.subscribe((frame) => {
    if (mode !== 'mesh' || !throttle(frame.t)) return;
    link.sendMesh(encodeMeshFrame({ seq: ++seq, t: frame.t, points: frame.landmarks ?? [] }));
  });

  const receiver = createMeshReceiver();
  let received = 0;
  let receivedAt = -Infinity;
  const offMesh = link.onMesh((data) => {
    const frame = decodeMeshFrame(data);
    const now = performance.now();
    if (frame && receiver.push(frame, now)) {
      received++;
      receivedAt = now;
    }
  });
  const ctx = meshCanvas.getContext('2d');
  const project = makeProjector({
    width: 640,
    height: 480,
    sourceAspect: 4 / 3,
    fit: 'contain',
    mirror: true,
  });
  let raf = 0;
  const draw = (): void => {
    const showMesh = !remoteHasVideo;
    remote.hidden = showMesh;
    meshCanvas.hidden = !showMesh;
    if (showMesh && ctx) {
      const now = performance.now();
      const points = receiver.faceVisible(now) ? (receiver.latest()?.points ?? []) : [];
      drawMesh(ctx, points, project, meshCanvas);
    }
    meshCanvas.dataset['frames'] = String(received);
    raf = requestAnimationFrame(draw);
  };
  raf = requestAnimationFrame(draw);

  return {
    element,
    controls,
    faceSource,
    localVideo: local,
    shareMode: () => mode,
    remoteVideo: remote,
    remoteFigure,
    localFigure,
    remoteShowsMesh: () => !remoteHasVideo,
    remoteMesh: () => {
      const last = receiver.latest();
      if (!last) return null;
      const now = performance.now();
      return { points: receiver.faceVisible(now) ? last.points : [], t: receivedAt };
    },
    meshStats: () => (remoteHasVideo ? '' : `maillage reçu : ${receiver.fps().toFixed(0)} img/s`),
    hideVideos(hidden) {
      for (const t of sentVideo) t.enabled = !hidden;
      localFigure.dataset['sending'] = hidden ? 'off' : 'on';
      remoteFigure.dataset['covered'] = String(hidden);
    },
    dispose() {
      for (const t of sentVideo) t.stop();
      cancelAnimationFrame(raf);
      faceSource.stop();
      offStream();
      offFace();
      offMesh();
    },
  };
}
